import { useState } from "react";
import { Phone, FileText, MessageCircle, X } from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { fmtPrice } from "../../assets/data";
import { apiFetch } from "../../api";
import LoadingSpinner from "../LoadingSpinner";
import useSiteSettings from "../../hooks/useSiteSettings";

function BookingModal({ p, lang, cur, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async () => {
    if (!f.name || !f.email) return;
    setSending(true);
    try {
      await apiFetch("/api/leads", {
        method: "POST",
        body: {
          name: f.name,
          email: f.email,
          phone: f.phone,
          projectId: p.id,
          message: `Viewing request for ${p.name}`,
        },
      });
      setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.72)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(5px)",
        animation: "fadeIn .2s ease",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "var(--navy)",
          border: "1px solid rgba(201,168,76,.2)",
          borderRadius: 18,
          padding: "2.5rem",
          width: "min(480px,95vw)",
          position: "relative",
          animation: "slideUp .3s ease",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "rgba(255,255,255,.06)",
            border: "none",
            borderRadius: "50%",
            width: 34,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,.4)",
            cursor: "pointer",
            transition: "background .2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.06)")}
        >
          <X size={14} />
        </button>

        {sent ? (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <div style={{ fontSize: "2.75rem", marginBottom: "1rem" }}>✨</div>
            <h3
              style={{
                fontFamily: "var(--serif)",
                color: "var(--gold)",
                fontSize: "1.7rem",
                fontWeight: 300,
                marginBottom: ".5rem",
              }}
            >
              {lang === "en" ? "Request Sent!" : "تم إرسال الطلب!"}
            </h3>
            <p style={{ color: "rgba(255,255,255,.42)", fontSize: ".86rem", lineHeight: 1.75 }}>
              {lang === "en"
                ? "Our team will contact you within 24 hours to schedule your visit."
                : "سيتواصل معك فريقنا خلال 24 ساعة لتحديد موعد الزيارة."}
            </p>
            <button
              className="btn-g"
              onClick={onClose}
              style={{ marginTop: "1.75rem", borderRadius: 8, padding: "10px 30px", fontSize: ".84rem" }}
            >
              {lang === "en" ? "Done" : "تم"}
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "1.75rem" }}>
              <div
                style={{
                  fontFamily: "var(--serif)",
                  color: "#fff",
                  fontSize: "1.55rem",
                  fontWeight: 300,
                  marginBottom: ".3rem",
                }}
              >
                {lang === "en" ? "Request a Callback" : "طلب معاودة الاتصال"}
              </div>
              <div style={{ color: "rgba(255,255,255,.3)", fontSize: ".78rem", fontFamily: "var(--sans)" }}>
                {lang === "ar" ? p.nameAr : p.name}
              </div>
            </div>
            <div className="gline" style={{ marginBottom: "1.5rem" }} />

            {[
              { k: "name",  type: "text",  ph: lang === "en" ? "Full Name *" : "الاسم الكامل *" },
              { k: "email", type: "email", ph: lang === "en" ? "Email Address *" : "البريد الإلكتروني *" },
              { k: "phone", type: "tel",   ph: lang === "en" ? "Phone Number" : "رقم الهاتف" },
            ].map(({ k, type, ph }) => (
              <input
                key={k}
                id={`cta-contact-${k}`}
                name={k}
                type={type}
                value={f[k]}
                onChange={set(k)}
                placeholder={ph}
                className="li-dark"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 8,
                  fontSize: ".87rem",
                  marginBottom: ".75rem",
                  display: "block",
                }}
              />
            ))}

            <button
              className="btn-g"
              onClick={submit}
              disabled={sending || !f.name || !f.email}
              style={{
                width: "100%",
                borderRadius: 8,
                padding: "13px",
                fontSize: ".87rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                marginTop: ".25rem",
                opacity: !f.name || !f.email ? 0.55 : 1,
                cursor: !f.name || !f.email ? "not-allowed" : "pointer",
              }}
            >
              {sending ? (
                <LoadingSpinner size={16} thickness={2} color="var(--navy)" trackColor="rgba(10,22,40,.3)" />
              ) : (
                lang === "en" ? "Send Request" : "إرسال الطلب"
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ContactCTA({ p }) {
  const { lang, cur } = useAppContext();
  const { whatsapp } = useSiteSettings();
  const [modalOpen, setModalOpen] = useState(false);

  const open = () => setModalOpen(true);
  const close = () => setModalOpen(false);

  return (
    <>
      {modalOpen && <BookingModal p={p} lang={lang} cur={cur} onClose={close} />}

      {/* ── Desktop sticky sidebar card ── */}
      <div
        className="pd-sidebar-col"
        style={{ position: "sticky", top: 80 }}
      >
        <div
          style={{
            background: "linear-gradient(160deg,var(--navy) 0%,var(--navy2) 100%)",
            border: "1px solid rgba(201,168,76,.22)",
            borderRadius: 18,
            padding: "2rem",
            boxShadow: "0 24px 64px rgba(10,22,40,.24)",
          }}
        >
          {/* Price */}
          <div
            style={{
              fontSize: ".58rem",
              color: "rgba(255,255,255,.33)",
              letterSpacing: ".22em",
              textTransform: "uppercase",
              fontFamily: "var(--sans)",
              marginBottom: ".4rem",
            }}
          >
            {lang === "en" ? "Starting From" : "يبدأ من"}
          </div>
          <div
            className="shimmer-text"
            style={{ fontFamily: "var(--serif)", fontSize: "2rem", fontWeight: 600, marginBottom: ".3rem" }}
          >
            {fmtPrice(p, cur)}
          </div>

          {p.installmentDuration && (
            <div
              style={{
                fontSize: ".76rem",
                color: "rgba(255,255,255,.38)",
                marginBottom: "1.5rem",
                fontFamily: "var(--sans)",
              }}
            >
              {lang === "en"
                ? `or ${p.installmentDuration}-month installment`
                : `أو تقسيط على ${p.installmentDuration} شهر`}
            </div>
          )}

          <div className="gline" style={{ marginBottom: "1.4rem" }} />

          {/* Payment mini-grid */}
          {(p.downPayment || p.deliveryYear) && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: p.downPayment && p.deliveryYear ? "1fr 1fr" : "1fr",
                gap: ".6rem",
                marginBottom: "1.4rem",
              }}
            >
              {p.downPayment && (
                <div
                  style={{
                    background: "rgba(255,255,255,.04)",
                    borderRadius: 10,
                    padding: ".9rem .8rem",
                    border: "1px solid rgba(255,255,255,.06)",
                  }}
                >
                  <div
                    style={{
                      fontSize: ".55rem",
                      color: "rgba(255,255,255,.3)",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      fontFamily: "var(--sans)",
                      marginBottom: ".35rem",
                    }}
                  >
                    {lang === "en" ? "Downpayment" : "المقدم"}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--serif)",
                      fontSize: ".95rem",
                      color: "var(--gold2)",
                    }}
                  >
                    {fmtPrice({ priceEGP: p.downPayment, priceSAR: null, priceAED: null }, "EGP")}
                  </div>
                </div>
              )}
              {p.deliveryYear && (
                <div
                  style={{
                    background: "rgba(255,255,255,.04)",
                    borderRadius: 10,
                    padding: ".9rem .8rem",
                    border: "1px solid rgba(255,255,255,.06)",
                  }}
                >
                  <div
                    style={{
                      fontSize: ".55rem",
                      color: "rgba(255,255,255,.3)",
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      fontFamily: "var(--sans)",
                      marginBottom: ".35rem",
                    }}
                  >
                    {lang === "en" ? "Delivery" : "التسليم"}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--serif)",
                      fontSize: ".95rem",
                      color: "var(--gold2)",
                    }}
                  >
                    {p.deliveryYear}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CTA buttons */}
          <button
            className="btn-g"
            onClick={open}
            style={{
              width: "100%",
              borderRadius: 10,
              padding: "13px",
              fontSize: ".86rem",
              marginBottom: ".6rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: ".5rem",
            }}
          >
            <Phone size={14} />
            {lang === "en" ? "Request a Call" : "طلب اتصال"}
          </button>

          <button
            className="btn-o"
            style={{
              width: "100%",
              borderRadius: 10,
              padding: "13px",
              fontSize: ".86rem",
              marginBottom: ".6rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: ".5rem",
            }}
          >
            <FileText size={14} />
            {lang === "en" ? "Download Brochure" : "تحميل البروشور"}
          </button>

          <a
            href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: ".5rem",
              padding: "11px",
              borderRadius: 10,
              background: "rgba(37,211,102,.1)",
              border: "1px solid rgba(37,211,102,.25)",
              color: "#25D366",
              fontSize: ".82rem",
              fontFamily: "var(--sans)",
              textDecoration: "none",
              transition: "background .2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(37,211,102,.18)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(37,211,102,.1)")}
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
        </div>
      </div>

      {/* ── Mobile fixed bottom bar ── */}
      <div className="pd-mobile-cta">
        <button
          className="btn-g"
          onClick={open}
          style={{
            flex: 1,
            borderRadius: 10,
            padding: "13px",
            fontSize: ".86rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: ".5rem",
          }}
        >
          <Phone size={14} />
          {lang === "en" ? "Request a Call" : "طلب اتصال"}
        </button>
        <button
          className="btn-o"
          style={{
            flex: 1,
            borderRadius: 10,
            padding: "13px",
            fontSize: ".86rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: ".5rem",
          }}
        >
          <FileText size={14} />
          {lang === "en" ? "Brochure" : "البروشور"}
        </button>
      </div>
    </>
  );
}
